import { Activity, AlertTriangle, Clock3, Gauge, Timer, Video } from "lucide-react";
import { portalApi, type PortalApiClient } from "../api/client";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../components/Shared";
import { useAsyncData } from "../hooks/useAsyncData";

function average(values: number[]): number {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

export function DashboardPage({ client = portalApi }: { client?: PortalApiClient }) {
  const metricsData = useAsyncData(() => client.metrics.throughput(), [client]);

  if (metricsData.loading) return <LoadingState label="Loading operations overview" />;
  if (metricsData.error || !metricsData.data) {
    return (
      <ErrorState
        message={metricsData.error ?? "No overview is available."}
        onRetry={metricsData.reload}
      />
    );
  }

  const data = metricsData.data;
  const today = new Date().toISOString().slice(0, 10);
  const todayRows = data.throughput.filter((row) => row.day.slice(0, 10) === today);
  const finishedMinutes = todayRows.reduce((sum, row) => sum + row.finishedMinutes, 0);
  const equivalentVideos = todayRows.reduce(
    (sum, row) => sum + row.equivalent15MinuteVideos,
    0,
  );
  const configuredSlots = data.utilization.reduce((sum, row) => sum + row.configuredSlots, 0);
  const activeAttempts = data.utilization.reduce((sum, row) => sum + row.activeAttempts, 0);
  const utilizationPercent = configuredSlots ? (activeAttempts / configuredSlots) * 100 : 0;
  const queueDelay = average(data.queueDelay.map((row) => row.averageQueueDelayMinutes));
  const stageDuration = average(data.stageDuration.map((row) => row.averageMinutes));

  const metrics = [
    {
      label: "Finished today",
      value: `${finishedMinutes.toLocaleString()} min`,
      detail: "Completed video minutes",
      icon: Video,
    },
    {
      label: "15-minute equivalents",
      value: equivalentVideos.toFixed(1),
      detail: "Measured output today",
      icon: Gauge,
    },
    {
      label: "Queue delay",
      value: `${queueDelay.toFixed(0)} min`,
      detail: "Average across channels",
      icon: Clock3,
    },
    {
      label: "Stage duration",
      value: `${stageDuration.toFixed(0)} min`,
      detail: "Average across active stages",
      icon: Timer,
    },
    {
      label: "Failure rate",
      value: `${data.failureRate.failureRatePercent.toFixed(1)}%`,
      detail: `${data.failureRate.failedAttempts} of ${data.failureRate.totalAttempts} attempts`,
      icon: AlertTriangle,
      warning: data.failureRate.failureRatePercent > 5,
    },
    {
      label: "Worker utilization",
      value: `${utilizationPercent.toFixed(0)}%`,
      detail: `${activeAttempts} active of ${configuredSlots} configured slots`,
      icon: Activity,
    },
  ];

  return (
    <>
      <PageHeader
        title="Operations overview"
        description="Current output, delays, and worker utilization. These are measured results, not targets."
      />
      <section className="metric-grid" aria-label="Daily performance">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <article className={`metric ${metric.warning ? "metric-warning" : ""}`} key={metric.label}>
              <div className="metric-label">
                <Icon size={17} aria-hidden="true" />
                <span>{metric.label}</span>
              </div>
              <strong>{metric.value}</strong>
              <small>{metric.detail}</small>
            </article>
          );
        })}
      </section>
      <section className="content-section">
        <div className="section-heading">
          <div>
            <h2>Output by channel</h2>
            <p>Finished video minutes and equivalent 15-minute videos.</p>
          </div>
        </div>
        {data.throughput.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Channel</th>
                  <th className="numeric">Finished videos</th>
                  <th className="numeric">Finished minutes</th>
                  <th className="numeric">15-minute equivalents</th>
                </tr>
              </thead>
              <tbody>
                {data.throughput.map((row) => (
                  <tr key={`${row.day}-${row.channelId}`}>
                    <td>{new Date(row.day).toLocaleDateString()}</td>
                    <td>{row.channelName}</td>
                    <td className="numeric">{row.finishedVideos}</td>
                    <td className="numeric">{row.finishedMinutes.toLocaleString()}</td>
                    <td className="numeric">{row.equivalent15MinuteVideos.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No completed output yet"
            message="Channel throughput appears here after the first completed video."
          />
        )}
      </section>
    </>
  );
}
